import type { CSSProperties } from "react";

import { hexOf, FONT_STACKS, type SocialDestination, type SocialStyle } from "../domain/canvas-model";
import { SOCIAL_ICONS, SOCIAL_BRAND_COLORS, SOCIAL_PLATFORM_LABELS } from "./social-icons";
import { useContentAnchorProps } from "./contentPointer";

/**
 * Shared SOCIAL block renderer used by BOTH the private Canvas editor and the
 * public Grid publication layer, so a Territory's social row renders identically
 * in both surfaces. Sizes (icon/label/gap/padding) are logical px multiplied by
 * the camera `scale`, exactly like AutoFitText, so the block zooms with the
 * Territory. Presets seed sensible defaults but every field below is authored
 * explicitly and always wins.
 *
 * `interactive` = true only on the public Grid: anchors become real links (with
 * pointer stop-propagation so a click never triggers a pan) and hover motion is
 * enabled. In the editor the block is inert (selection/drag handled by the hit
 * proxy overlay).
 */
export function SocialRender({
  destinations, style, scale, interactive,
}: {
  destinations: SocialDestination[]; style: SocialStyle; scale: number; interactive: boolean;
}) {
  const anchorProps = useContentAnchorProps();
  const px = (n: number) => Math.max(0, n * scale);
  const s = style;
  const iconSize = Math.max(4, px(s.icon_size));
  const fontSize = Math.max(1, px(s.font_size));
  const gap = px(s.gap);
  const pad = px(s.padding);
  const showLabel = s.show_label && s.preset !== "ICONS_ONLY";
  const itemVertical = s.preset === "CARDS";
  const hasChrome = s.bg_enabled || s.border_enabled;
  const radius = s.radius === "PILL" ? 9999 : s.radius === "ROUNDED" ? 12 * scale : s.radius === "SLIGHT" ? 5 * scale : 0;
  const bg = s.bg_enabled ? hexOf(s.bg_color, "#2b3340") : "transparent";
  const border = s.border_enabled ? `${Math.max(1, 1.5 * scale)}px solid ${hexOf(s.border_color, "#8a97a8")}` : "none";
  const labelColor = hexOf(s.label_color, "#f5f7fa");
  const labelFont = FONT_STACKS[s.font] ?? FONT_STACKS.GRID_SANS;
  const justify = s.align === "START" ? "flex-start" : s.align === "END" ? "flex-end" : "center";

  const container: CSSProperties = s.direction === "GRID"
    ? { display: "inline-grid", gridTemplateColumns: `repeat(${Math.max(1, s.columns)}, minmax(0, max-content))`, gap, justifyContent: justify, alignContent: "center", placeItems: "center", boxSizing: "border-box" }
    : { display: "inline-flex", flexDirection: s.direction === "VERTICAL" ? "column" : "row", flexWrap: "nowrap", gap, alignItems: s.direction === "VERTICAL" ? (justify as CSSProperties["alignItems"]) : "center", justifyContent: justify, boxSizing: "border-box" };

  return (
    <div style={container}>
      {destinations.map((d, i) => {
        const Icon = SOCIAL_ICONS[d.platform] ?? SOCIAL_ICONS.website;
        const iconColor = d.icon_color
          ? hexOf(d.icon_color)
          : s.use_brand_colors ? (SOCIAL_BRAND_COLORS[d.platform] ?? "#f5f7fa") : hexOf(s.icon_color, "#f5f7fa");
        const label = (d.label && d.label.trim()) || SOCIAL_PLATFORM_LABELS[d.platform] || d.platform;
        const item = (
          <span
            className={interactive && s.hover !== "NONE" ? `social-item hv-${s.hover.toLowerCase()}` : "social-item"}
            style={{
              display: "inline-flex", flexDirection: itemVertical ? "column" : "row",
              alignItems: "center", justifyContent: "center", gap: px(6),
              background: bg, border, borderRadius: radius,
              padding: hasChrome ? `${px(8)}px ${pad}px` : 0,
              color: iconColor, boxSizing: "border-box", maxWidth: "100%", textDecoration: "none",
            }}
          >
            <Icon size={iconSize} color={iconColor} style={{ flexShrink: 0 }} />
            {showLabel && (
              <span style={{ fontFamily: labelFont, fontSize, color: labelColor, lineHeight: 1.1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{label}</span>
            )}
          </span>
        );
        if (interactive && typeof d.url === "string" && d.url.startsWith("https://")) {
          return (
            <a key={i} data-testid="grid-pub-social-link" href={d.url} target="_blank" rel="noopener noreferrer"
              {...anchorProps}
              style={{ display: "inline-flex", pointerEvents: "auto", textDecoration: "none", cursor: "pointer" }}>
              {item}
            </a>
          );
        }
        return <span key={i} style={{ display: "inline-flex" }}>{item}</span>;
      })}
    </div>
  );
}
